import { useState } from 'react';
import type { CellAttachment } from './types';
import { normalizeAttachments, isImageAttachment, isPdfAttachment, formatFileSize } from './types';
import { MediaLightboxModal } from './MediaLightboxModal';
import { useI18n } from '@/shared/i18n/useT';
import { useResolvedAttachments } from '@/shared/files/attachments';

export interface AttachmentCellProps {
  readonly value: unknown;
  readonly editable?: boolean | undefined;
  readonly onChange?: ((attachments: CellAttachment[]) => void) | undefined;
  readonly onOpenUpload?: (() => void) | undefined;
  /** An upload into this cell is in flight. */
  readonly uploading?: boolean | undefined;
}

/** The viewer, with stored files fetched at full size only once it opens. */
function ResolvedLightbox(props: { attachments: readonly CellAttachment[]; initialIndex: number; onClose: () => void; onDelete?: ((index: number) => void) | undefined }) {
  const attachments = useResolvedAttachments(props.attachments, 'full');
  return <MediaLightboxModal {...props} attachments={attachments} />;
}

export function AttachmentCell({ value, editable, onChange, onOpenUpload, uploading }: AttachmentCellProps) {
  const { t } = useI18n();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const attachments = normalizeAttachments(value);
  const shown = useResolvedAttachments(attachments, 'preview');

  if (uploading) {
    return <span role="status" className="text-xs text-muted">{t('attachments.uploading')}</span>;
  }

  if (attachments.length === 0) {
    if (!editable) return null;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenUpload?.();
        }}
        className="inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs text-muted hover:bg-surface-3 hover:text-fg transition-colors"
        title={t('attachments.upload')}
        aria-label={t('attachments.upload')}
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span className="text-[11px] opacity-70 group-hover/cell:opacity-100">{t('attachments.upload')}</span>
      </button>
    );
  }

  const primary = shown[0]!;
  const extraCount = attachments.length - 1;
  const isImg = isImageAttachment(primary);
  const isPdf = isPdfAttachment(primary);

  const openAt = (idx: number) => {
    setLightboxIndex(idx);
    setLightboxOpen(true);
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.stopPropagation();
          openAt(0);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            openAt(0);
          }
        }}
        className="group/att flex w-full min-w-0 items-center gap-1.5 overflow-hidden rounded-sm py-0.5 text-left focus:outline-none focus:ring-1 focus:ring-brand cursor-pointer"
        title={attachments.map((a) => `${a.name} (${formatFileSize(a.size)})`).join('\n')}
      >
        {/* Miniature Preview Thumbnail or PDF Badge */}
        <div className="relative flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded border border-border/80 bg-surface-2">
          {isImg ? (
            <img
              src={primary.thumbnail || primary.url || undefined}
              alt={primary.name}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : isPdf ? (
            <span className="flex h-full w-full items-center justify-center bg-danger/10 text-[9px] font-bold text-danger">
              PDF
            </span>
          ) : (
            <svg className="h-3.5 w-3.5 text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
          )}
        </div>

        {/* Truncated File Label */}
        <span className="min-w-0 flex-1 truncate text-xs text-fg">
          {primary.name}
        </span>

        {/* Multi-Attachment Badge (+N) */}
        {extraCount > 0 && (
          <span className="shrink-0 rounded-full border border-border bg-surface-3 px-1.5 py-0.2 text-[10px] font-semibold text-muted">
            {`+${extraCount}`}
          </span>
        )}

        {editable && onOpenUpload && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpenUpload();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            className="shrink-0 rounded-sm p-0.5 text-muted opacity-0 transition-opacity hover:bg-surface-3 hover:text-fg focus:opacity-100 group-hover/cell:opacity-100"
            title={t('attachments.upload')}
            aria-label={t('attachments.upload')}
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
        )}
      </div>

      {lightboxOpen && (
        <ResolvedLightbox
          attachments={attachments}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          onDelete={editable && onChange ? (deletedIndex) => {
            const next = attachments.filter((_, idx) => idx !== deletedIndex);
            onChange(next);
            if (next.length === 0) setLightboxOpen(false);
          } : undefined}
        />
      )}
    </>
  );
}
